// Generated by tutorials/record.cjs from tutorials/articles/h03-ehr-connection.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h03 --text-only` (wording) or
// `node tutorials/record.cjs h03` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'ehr-connection',
  catalogId: 'h03',
  title: 'How your EHR connection works',
  summary: 'When your facility is connected to its EHR, admissions, moves, discharges, diet orders and allergies reach Dietech on their own. Here is what comes in, what your staff still do, which change wins when they disagree, and what to check when a diet does not change.',
  audience: 'Dietary managers and dietitians at facilities connected to an EHR',
  area: 'residents',
  learn: [
    'What your EHR sends and what Dietech does with it',
    'How quickly it arrives',
    'What your staff still keep up to date by hand',
    'Which change wins when your staff and your EHR disagree',
    'Where to see what came in',
    'What to check when a resident\'s diet did not change',
  ],
  keywords: [
    'ehr',
    'emr',
    'interface',
    'connection',
    'integration',
    'admission',
    'discharge',
    'transfer',
    'readmit',
    'diet order',
    'diet fluctuations',
    'allergy',
    'lock',
    'locked',
    'held',
    'held changes',
    'override',
    'manual',
    'preadmission',
    'outpatient',
    'not updating',
    'did not change',
  ],
  steps: [
    {
      id: 'overview',
      part: 'What comes in from your EHR',
      heading: 'Your EHR keeps Dietech up to date',
      text: 'When your facility is connected to its EHR, your EHR tells Dietech each time a resident is admitted, moves, is discharged or gets a new diet order. Dietech makes the change for you, so nobody types it twice.\n\nYour dashboard shows what changed since your last shift: Diet Order Changes, Admitted Residents and Unit/Room/Bed Changes.',
      media: {
        image: {
          src: '/help/ehr-connection/overview.webp',
          width: 1280,
          height: 684,
        },
        alt: 'The dashboard with the Diet Order Changes, Admitted Residents and Unit/Room/Bed Changes cards.',
      },
    },
    {
      id: 'admissions',
      part: 'What comes in from your EHR',
      heading: 'Admissions and readmissions',
      text: 'A new admission adds the resident with their unit, room and bed, their diet order and their food allergies.\n\nWhen a discharged resident comes back, Dietech brings back the record you already have, with everything your staff entered. Admitted Residents marks them Readmit.',
      media: {
        image: {
          src: '/help/ehr-connection/admissions.webp',
          width: 1216,
          height: 234,
        },
        alt: 'The Admitted Residents card: Navarro, Dolores, a new admission, and Ortega, Luis, marked Yes under Readmit.',
      },
    },
    {
      id: 'moves',
      part: 'What comes in from your EHR',
      heading: 'Moves and discharges',
      text: 'When your EHR moves a resident to another unit, room or bed, Dietech moves them too, so the tray goes to the right place. Unit/Room/Bed Changes lists each move.\n\nWhen your EHR discharges a resident, Dietech discharges them and their trays stop.',
      media: {
        image: {
          src: '/help/ehr-connection/moves.webp',
          width: 1216,
          height: 278,
        },
        alt: 'The Unit/Room/Bed Changes card listing three moves, with the unit, room and bed each resident moved to.',
      },
    },
    {
      id: 'diet-orders',
      part: 'What comes in from your EHR',
      heading: 'Diet orders',
      text: 'Each diet order your EHR sends sets the resident\'s diet types and consistencies, and is kept in Diet Fluctuations with the date it starts, newest first.\n\nType says who entered each order: EHR for your EHR, Manual for your staff. EHR Diet Order shows the order the way your EHR wrote it. Some facilities do not show these two columns.',
      media: {
        image: {
          src: '/help/ehr-connection/diet-orders.webp',
          width: 1280,
          height: 294,
        },
        clip: {
          src: '/help/ehr-connection/diet-orders.mp4',
          width: 1280,
          height: 294,
          durationSec: 6,
        },
        alt: 'Diet Fluctuations for Delgado, Rosa: two orders of Type EHR with the text your EHR sent, and one Manual order between them.',
      },
    },
    {
      id: 'allergies',
      part: 'What comes in from your EHR',
      heading: 'Food allergies',
      text: 'Allergies your EHR sends are added to the resident\'s Food Allergies and marked from EHR. Allergies your staff entered have no mark.\n\nEvery allergy your EHR adds or takes off is also listed on the EHR page under Allergy Changes, for a dietitian to review.',
      media: {
        image: {
          src: '/help/ehr-connection/allergies.webp',
          width: 1280,
          height: 202,
        },
        clip: {
          src: '/help/ehr-connection/allergies.mp4',
          width: 1280,
          height: 202,
          durationSec: 6,
        },
        alt: 'Food Allergies for Chen, Mei: Allergy 1, Shellfish, marked from EHR, and Allergy 2, Eggs, entered by staff, with no mark.',
      },
    },
    {
      id: 'more',
      part: 'What comes in from your EHR',
      heading: 'Supplements, tube feeds and weights',
      text: 'When it is turned on for your facility, the supplement and tube feed orders your EHR sends are added to the resident\'s meal pattern and tube feed for you. The article Automatic supplements and tube feeds from your EHR covers it.\n\nIf your EHR sends weights, they are added to the resident\'s Weight History.',
      textOnly: true,
    },
    {
      id: 'how-fast',
      part: 'What comes in from your EHR',
      heading: 'How quickly it arrives',
      text: 'Most changes reach Dietech within minutes of being entered in your EHR.\n\nOnly something that needs a person waits, such as wording Dietech has not seen before. Pending Messages on the EHR page lists anything still waiting, and it refreshes by itself.',
      media: {
        image: {
          src: '/help/ehr-connection/how-fast.webp',
          width: 1280,
          height: 218,
        },
        alt: 'The Pending Messages tab of the EHR page saying No EHR messages waiting, with the time of the next refresh.',
      },
    },
    {
      id: 'by-hand',
      part: 'What your staff still do',
      heading: 'What your EHR does not send',
      text: 'Your EHR does not send likes and dislikes, meal pattern items (apart from supplements, where that is turned on), adjustments, tray ticket notes, the tray line order or Selective Touch choices. Keep these up to date in Dietech, as you do now. A new order from your EHR leaves them as they are.\n\nIf your facility uses Auto Item Mods, a diet change from your EHR can update the meal pattern, adjustments or selective menu the way your rules say, just as a change your staff make would.',
      media: {
        image: {
          src: '/help/ehr-connection/by-hand.webp',
          width: 388,
          height: 266,
        },
        alt: 'The Meals part of a resident\'s sections: Meal Pattern, Adjustments, Selective Menu and Tray Ticket Notes.',
      },
    },
    {
      id: 'newest-wins',
      part: 'Which change wins',
      heading: 'The newest diet order wins',
      text: 'Dietech always uses the newest diet order. If your staff change a diet and your EHR then sends an order that changes it again, your EHR\'s order takes over, even when your EHR dated it earlier than your change. Your staff\'s order stays in the list, so you can see what happened.\n\nHere staff tried Rosa on a regular texture, then her EHR order for mechanical soft came in, so mechanical soft is her diet now.',
      media: {
        image: {
          src: '/help/ehr-connection/newest-wins.webp',
          width: 1280,
          height: 294,
        },
        alt: 'Diet Fluctuations for Delgado, Rosa: the newest order, from the EHR, is mechanical soft, above the Manual order for a regular texture.',
      },
    },
    {
      id: 'change-in-ehr',
      part: 'Which change wins',
      heading: 'To make your change last',
      text: 'Ask whoever writes orders in your EHR to change the order there too. Then the next order from your EHR agrees with yours.\n\nIf your facility uses diet order locking, you can also lock the order in Dietech.',
      textOnly: true,
    },
    {
      id: 'lock',
      part: 'Which change wins',
      heading: 'Lock a diet order',
      text: 'On Diet Fluctuations, select Lock. While the order is locked, new diet orders from your EHR are held for you to review instead of replacing it.\n\nIf your EHR sends the same diet within the hours your facility set, here 24, the lock comes off by itself, because your EHR has caught up.',
      media: {
        image: {
          src: '/help/ehr-connection/lock.webp',
          width: 942,
          height: 468,
        },
        clip: {
          src: '/help/ehr-connection/lock.mp4',
          width: 942,
          height: 468,
          durationSec: 2,
        },
        alt: 'The Lock Diet Order window: while locked, EHR diet changes are held for review instead of replacing this order.',
      },
    },
    {
      id: 'held-card',
      part: 'Which change wins',
      heading: 'Held changes are on your dashboard',
      text: 'Each order your EHR sends while a diet order is locked is listed on your dashboard under Held EHR diet changes, and on the resident\'s Diet Fluctuations under Held EHR changes. Select Review to decide.',
      media: {
        image: {
          src: '/help/ehr-connection/held-card.webp',
          width: 1280,
          height: 124,
        },
        clip: {
          src: '/help/ehr-connection/held-card.mp4',
          width: 1280,
          height: 124,
          durationSec: 3.1,
        },
        alt: 'The Held EHR diet changes card on the dashboard: Achebe, Raymond, with one held order and a Review button.',
      },
    },
    {
      id: 'held-review',
      part: 'Which change wins',
      heading: 'Keep yours, or use the new order',
      text: 'The window shows the current diet order and the held one from your EHR. Choose Use the new diet order to apply it and take off the lock, or Keep the current diet order, then select Save. Either way the held order is marked reviewed.',
      media: {
        image: {
          src: '/help/ehr-connection/held-review.webp',
          width: 942,
          height: 666,
        },
        clip: {
          src: '/help/ehr-connection/held-review.mp4',
          width: 942,
          height: 666,
          durationSec: 2,
        },
        alt: 'The Held EHR diet changes window for Achebe, Raymond: his current order with mildly thick liquids, the held order with thin liquids, and the two choices.',
      },
    },
    {
      id: 'allergy-wins',
      part: 'Which change wins',
      heading: 'Allergies your staff entered stay',
      text: 'Dietech never takes off an allergy your staff entered. Allergies marked from EHR follow your EHR: when your EHR takes one off, Dietech can take it off too.\n\nIf you change an allergy marked from EHR, Dietech asks first, because from then on your EHR no longer updates it.',
      media: {
        image: {
          src: '/help/ehr-connection/allergy-wins.webp',
          width: 708,
          height: 350,
        },
        clip: {
          src: '/help/ehr-connection/allergy-wins.mp4',
          width: 708,
          height: 350,
          durationSec: 2,
        },
        alt: 'The Verify Allergy Change window: this allergy is updated by EHR, and if changed it will no longer be updated automatically. Keep and Change buttons.',
      },
    },
    {
      id: 'ehr-page',
      part: 'See what came in',
      heading: 'The EHR page',
      text: 'In the menu, select Residents, then EHR. Message History lists what your EHR sent, and Pending Messages anything still waiting.\n\nThe number on Mappings is wording waiting to be mapped, red when it is holding orders back. The numbers on Allergy Changes and Added Items are changes waiting for your review. On a resident, the EHR section lists the messages for that resident.',
      media: {
        image: {
          src: '/help/ehr-connection/ehr-page.webp',
          width: 1280,
          height: 144,
        },
        alt: 'The EHR page tabs: Pending Messages, Message History, Mappings with a red 2, Allergy Changes with 3 and Added Items with 5.',
      },
    },
    {
      id: 'waiting-text',
      part: 'Why didn\'t my resident\'s diet change?',
      heading: 'The wording is waiting to be mapped',
      text: 'When your EHR uses wording Dietech has not seen before, the order waits until someone on your staff says what it means. The number on Mappings turns red.\n\nSelect Map waiting text and map it, as Map EHR text to Dietech shows. The waiting orders then go through by themselves.',
      media: {
        image: {
          src: '/help/ehr-connection/waiting-text.webp',
          width: 1280,
          height: 154,
        },
        alt: 'The Mappings tab with a red 2, and the banner: 4 phrases are waiting for mapping and are holding 5 messages, with the Map waiting text button.',
      },
    },
    {
      id: 'not-added',
      part: 'Why didn\'t my resident\'s diet change?',
      heading: 'The resident is not in Dietech yet',
      text: 'If your EHR sends a resident before they have a room and bed, such as a preadmission, Dietech waits to add them. Once your EHR sends their room and bed, they are added, and the diet orders and allergies sent in the meantime are applied.\n\nPatients on units your facility does not serve, such as outpatient areas, are not added at all.',
      textOnly: true,
    },
    {
      id: 'starts-later',
      part: 'Why didn\'t my resident\'s diet change?',
      heading: 'The order starts later',
      text: 'An order your EHR dates for later is listed at the top of Diet Fluctuations with its start date. The resident keeps their current diet until then, and the new one takes over at that time.',
      media: {
        image: {
          src: '/help/ehr-connection/starts-later.webp',
          width: 1280,
          height: 294,
        },
        alt: 'Diet Fluctuations for Ellison, Marjorie: a Renal order from the EHR dated tomorrow at 6:00 AM, above her current order.',
      },
    },
    {
      id: 'locked',
      part: 'Why didn\'t my resident\'s diet change?',
      heading: 'The diet order is locked',
      text: 'If a lock shows on the resident\'s current order in Diet Fluctuations, your EHR\'s new order is waiting under Held EHR changes. Review it to use it or keep yours.',
      media: {
        image: {
          src: '/help/ehr-connection/locked.webp',
          width: 1280,
          height: 294,
        },
        alt: 'Diet Fluctuations for Achebe, Raymond: his current order is locked, and Held EHR changes (1) is in the toolbar.',
      },
    },
    {
      id: 'ask-support',
      part: 'Why didn\'t my resident\'s diet change?',
      heading: 'Still not right? Ask Dietech support',
      text: 'First check that the order was entered in your EHR, and when it starts. Mapping your EHR\'s wording is your facility\'s job, but Dietech support is glad to help: with wording you are not sure about, a message that did not do what you expected, or what your connection takes in.\n\nIn Help, Support Hours shows whether support is open now, with the phone number and email address.',
      textOnly: true,
    },
  ],
  tips: [
    'Want a staff change to last? Change the order in your EHR too, or lock the diet order in Dietech if your facility uses locking. Otherwise the next order from your EHR replaces it.',
    'Check the Mappings badge and Allergy Changes on the EHR page once a day, the way you read your dashboard.',
    'Not sure which of these your facility gets? Ask your administrator or Dietech support.',
  ],
  notes: [
    'The connection only reads from your EHR. Nothing you do in Dietech changes your EHR.',
    'What comes in depends on your facility\'s setup: some facilities do not take allergies, moves or supplements from their EHR, and diet order locking is turned on only where the facility asks for it.',
    'Mapping is your facility\'s job: your staff decide what each phrase from your EHR means. Dietech support is glad to help with phrases you are not sure about.',
  ],
  related: [
    'map-ehr-text',
    'ehr-supplements-tube-feeds',
    'change-diet-order',
    'allergies-likes-dislikes',
    'discharge-readmit',
    'your-dashboard',
  ],
  recorded: '2026-10-28',
};

export default article;
